import { defineMessages } from 'react-intl';
/**
 * Official weather warnings: the warnings a national weather service issues for
 * a place (AEMET, DWD, Météo-France via MeteoAlarm, NOAA/NWS…), shown on the
 * station and location dashboards and on the warnings list. Not the user's own
 * station alerts, which live in `alerts` ("My alerts").
 *
 * Built by `packages/core/src/utils/WeatherWarnings/view.ts`.
 */
export default defineMessages({
    // ------------------------------------------------------------ hazards
    hazardWind: {
        id: 'weatherWarnings.hazardWind',
        defaultMessage: 'Wind'
    },
    hazardRain: {
        id: 'weatherWarnings.hazardRain',
        defaultMessage: 'Rain'
    },
    hazardThunderstorm: {
        id: 'weatherWarnings.hazardThunderstorm',
        defaultMessage: 'Thunderstorms'
    },
    hazardFlood: {
        id: 'weatherWarnings.hazardFlood',
        defaultMessage: 'Floods'
    },
    hazardSnowIce: {
        id: 'weatherWarnings.hazardSnowIce',
        defaultMessage: 'Snow and ice'
    },
    hazardAvalanche: {
        id: 'weatherWarnings.hazardAvalanche',
        defaultMessage: 'Avalanches'
    },
    hazardCoastalEvent: {
        id: 'weatherWarnings.hazardCoastalEvent',
        defaultMessage: 'Coastal hazards'
    },
    hazardFog: {
        id: 'weatherWarnings.hazardFog',
        defaultMessage: 'Fog'
    },
    hazardHighTemperature: {
        id: 'weatherWarnings.hazardHighTemperature',
        defaultMessage: 'High temperatures'
    },
    hazardLowTemperature: {
        id: 'weatherWarnings.hazardLowTemperature',
        defaultMessage: 'Low temperatures'
    },
    hazardForestFire: {
        id: 'weatherWarnings.hazardForestFire',
        defaultMessage: 'Forest fires'
    },
    hazardUv: {
        id: 'weatherWarnings.hazardUv',
        defaultMessage: 'UV radiation'
    },
    hazardAirQuality: {
        id: 'weatherWarnings.hazardAirQuality',
        defaultMessage: 'Air quality'
    },
    hazardOther: {
        id: 'weatherWarnings.hazardOther',
        defaultMessage: 'Other hazard'
    },
    // ------------------------------------------------------------ severity words
    severityMinor: {
        id: 'weatherWarnings.severityMinor',
        defaultMessage: 'Minor'
    },
    severityModerate: {
        id: 'weatherWarnings.severityModerate',
        defaultMessage: 'Moderate'
    },
    severitySevere: {
        id: 'weatherWarnings.severitySevere',
        defaultMessage: 'Severe'
    },
    severityExtreme: {
        id: 'weatherWarnings.severityExtreme',
        defaultMessage: 'Extreme'
    },
    // ------------------------------------------------------------ when
    fromTime: {
        id: 'weatherWarnings.fromTime',
        defaultMessage: 'From {time}'
    },
    severityNow: {
        id: 'weatherWarnings.severityNow',
        defaultMessage: '{severity} now'
    },
    severityFrom: {
        id: 'weatherWarnings.severityFrom',
        defaultMessage: '{severity} from {time}'
    },
    severityUntil: {
        id: 'weatherWarnings.severityUntil',
        defaultMessage: '{severity} until {time}'
    },
    // ------------------------------------------------------------ list
    activeNow: {
        id: 'weatherWarnings.activeNow',
        defaultMessage: 'Active now'
    },
    upcoming: {
        id: 'weatherWarnings.upcoming',
        defaultMessage: 'Upcoming'
    },
    lastUpdate: {
        id: 'weatherWarnings.lastUpdate',
        defaultMessage: 'Last update: {time}'
    },
    source: {
        id: 'weatherWarnings.source',
        defaultMessage: 'Source: {source}'
    },
    // ------------------------------------------------------------ screen reader
    spokenLabel: {
        id: 'weatherWarnings.spokenLabel',
        defaultMessage: '{count, plural, one {# official weather warning} other {# official weather warnings}}. {hazards}'
    },
    spokenHazardOne: {
        id: 'weatherWarnings.spokenHazardOne',
        defaultMessage: '{hazard}: {status}.'
    },
    spokenHazardTwo: {
        id: 'weatherWarnings.spokenHazardTwo',
        defaultMessage: '{hazard}: {first}, then {second}.'
    },
    spokenLabelArea: {
        id: 'weatherWarnings.spokenLabelArea',
        defaultMessage: '{label} {area}.'
    },
    spokenPeriod: {
        id: 'weatherWarnings.spokenPeriod',
        defaultMessage: '{severity}: {range}'
    },
    badgeLabel: {
        id: 'weatherWarnings.badgeLabel',
        defaultMessage: '{others, plural, =0 {Official warning, {severity}: {hazard}} other {Official warning, {severity}: {hazard}, and # more}}'
    },
    badgeLabelUpcoming: {
        id: 'weatherWarnings.badgeLabelUpcoming',
        defaultMessage: '{others, plural, =0 {Upcoming official warning, {severity}: {hazard}} other {Upcoming official warning, {severity}: {hazard}, and # more}}'
    },
    // ------------------------------------------------------------ titles
    title: {
        id: 'weatherWarnings.title',
        defaultMessage: 'Official weather warnings'
    },
    crumb: {
        id: 'weatherWarnings.crumb',
        defaultMessage: 'Official warnings'
    },
    viewAll: {
        id: 'weatherWarnings.viewAll',
        defaultMessage: 'All official warnings'
    },
    moreHazards: {
        id: 'weatherWarnings.moreHazards',
        defaultMessage: '+ {count} more: {hazards}'
    },
    // ------------------------------------------------------------ area
    areaThisLocation: {
        id: 'weatherWarnings.areaThisLocation',
        defaultMessage: '{area} (this location)'
    },
    areaYourStation: {
        id: 'weatherWarnings.areaYourStation',
        defaultMessage: '{area} (your station)'
    },
    otherAreas: {
        id: 'weatherWarnings.otherAreas',
        defaultMessage: '{count, plural, one {+ # other area} other {+ # other areas}}'
    },
    areaCovered: {
        id: 'weatherWarnings.areaCovered',
        defaultMessage: 'Area covered'
    },
    showAllAreas: {
        id: 'weatherWarnings.showAllAreas',
        defaultMessage: '{count, plural, one {Show # area} other {Show all # areas}}'
    },
    showAllOnMap: {
        id: 'weatherWarnings.showAllOnMap',
        defaultMessage: '{count, plural, one {Show # area on the map} other {Show all # areas on the map}}'
    },
    mapLabel: {
        id: 'weatherWarnings.mapLabel',
        defaultMessage: '{count, plural, =1 {Map of the area covered: {area}.} other {Map of the # areas covered. {area} is highlighted.}}'
    },
    mapLabelNoHighlight: {
        id: 'weatherWarnings.mapLabelNoHighlight',
        defaultMessage: '{count, plural, =1 {Map of the area covered.} other {Map of the # areas covered.}}'
    },
    mapUnavailable: {
        id: 'weatherWarnings.mapUnavailable',
        defaultMessage: '{count, plural, =1 {Map unavailable. The area is listed below.} other {Map unavailable. The # areas are listed below.}}'
    },
    // ------------------------------------------------------------ time
    todayAt: {
        id: 'weatherWarnings.todayAt',
        defaultMessage: 'today at {time}'
    },
    tomorrowAt: {
        id: 'weatherWarnings.tomorrowAt',
        defaultMessage: 'tomorrow at {time}'
    },
    tomorrow: {
        id: 'weatherWarnings.tomorrow',
        defaultMessage: 'Tomorrow'
    },
    showPeriods: {
        id: 'weatherWarnings.showPeriods',
        defaultMessage: '{count, plural, one {Show # period} other {Show all # periods}}'
    },
    // ------------------------------------------------------------ detail
    whatToDo: {
        id: 'weatherWarnings.whatToDo',
        defaultMessage: 'What to do'
    },
    agencyMessage: {
        id: 'weatherWarnings.agencyMessage',
        defaultMessage: 'Message from the agency'
    },
    notInLanguage: {
        id: 'weatherWarnings.notInLanguage',
        defaultMessage: 'Not available in {language}. Shown as the agency published it.'
    },
    issuedBy: {
        id: 'weatherWarnings.issuedBy',
        defaultMessage: 'Issued by {sender}'
    },
    openOn: {
        id: 'weatherWarnings.openOn',
        defaultMessage: 'Open on {site}'
    },
    ended: {
        id: 'weatherWarnings.ended',
        defaultMessage: 'Ended'
    },
    endedAt: {
        id: 'weatherWarnings.endedAt',
        defaultMessage: 'This warning ended {time}.'
    },
    // ------------------------------------------------------------ banner
    showHazards: {
        id: 'weatherWarnings.showHazards',
        defaultMessage: 'Show hazards'
    },
    hideHazards: {
        id: 'weatherWarnings.hideHazards',
        defaultMessage: 'Hide hazards'
    },
    // ------------------------------------------------------------ states
    emptyTitle: {
        id: 'weatherWarnings.emptyTitle',
        defaultMessage: 'No official warnings'
    },
    emptyBody: {
        id: 'weatherWarnings.emptyBody',
        defaultMessage: 'The national weather service has no warning in force for this place. We check again every few minutes.'
    },
    errorTitle: {
        id: 'weatherWarnings.errorTitle',
        defaultMessage: 'Couldn’t load official warnings'
    },
    errorBody: {
        id: 'weatherWarnings.errorBody',
        defaultMessage: 'Check your connection. We try again every few minutes.'
    }
});
